import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5index from "@amcharts/amcharts5/index";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { isPlatformBrowser } from '@angular/common';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';



/**
 * Lollipop Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-lollipop-graph',
  standalone: false,
  templateUrl: './lollipop-graph.component.html',
  styleUrl: './lollipop-graph.component.scss'
})
export class LollipopGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private lollipopgraphReportSubscription: any;
  private isLollipopViewInitialized = false;
  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.lollipopgraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Lollipop Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isLollipopViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isLollipopViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isLollipopViewInitialized) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  createChart(data: BarChartData[]) {
    this.root = this._baseChartService.createRoot('lollipop-chartdiv');
    this._baseChartService.attachExporting(this.root, { 
      filePrefix: 'lollipop',
      data: data
    });

    this.root.setThemes([am5themes_Animated.new(this.root)]);



    let chart = this.root.container.children.push(am5xy.XYChart.new(this.root, {
      panX: true,
      panY: true,
      wheelX: "panX",
      wheelY: "zoomX",
      pinchZoomX: true,
      paddingLeft: 0
    }));

    // Enhanced chart animation
    this._baseChartService.setupChartAnimation(chart, 'bounce');

    let cursor = chart.set("cursor", am5xy.XYCursor.new(this.root, {}));
    cursor.lineY.set("visible", false);

    let xRenderer = am5xy.AxisRendererX.new(this.root, {
      minGridDistance: 20,
      minorGridEnabled: true
    });
    
    xRenderer.labels.template.setAll({
      rotation: -45,
      centerY: am5.p100,
      centerX: am5.p50,
      fontSize: 10,
      dy: 10,
      maxWidth: 80,
      textAlign: "center",
      oversizedBehavior: "truncate"
    });
    
    xRenderer.grid.template.setAll({
      visible: false
    });

    let xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(this.root, {
      maxDeviation: 0.3,
      categoryField: "category",
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(this.root, {})
    }));

    // Enhanced axis animation
    this._baseChartService.setupAxisAnimation(xAxis);

    let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(this.root, {
      maxDeviation: 0.3,
      renderer: am5xy.AxisRendererY.new(this.root, {})
    }));

    // Enhanced axis animation for y axis
    this._baseChartService.setupAxisAnimation(yAxis);

    // Create series
    let series = chart.series.push(am5xy.ColumnSeries.new(this.root, {
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: "value",
      categoryXField: "category",
      adjustBulletPosition: false,
      tooltip: am5.Tooltip.new(this.root, {
        labelText: "{valueY}"
      })
    }));

    // Enhanced series animation
    this._baseChartService.setupSeriesAnimation(series, 'elastic');

    series.columns.template.setAll({
      width: 0.5
    });

    series.bullets.push(() => {
      return am5.Bullet.new(this.root, {
        locationY: 1,
        sprite: am5.Circle.new(this.root, {
          radius: 5,
          fill: series.get("fill")
        })
      })
    });

    // Configure tooltip with enhanced animation
    const tooltip = am5.Tooltip.new(this.root, {
      labelText: "{valueY}"
    });
    this._baseChartService.setupTooltipAnimation(tooltip);
    series.set('tooltip', tooltip);

    // Set data
    const categoryField = this._baseChartService.getCategoryField(data);
    const valueFields = this._baseChartService.getValueFields(data);
    const firstValueField = valueFields[0];

    // Transform data for lollipop chart
    const lollipopData = data.map((item, index) => ({
      category: item[categoryField],
      value: Number(item[firstValueField]) || 0
    }));

    xAxis.data.setAll(lollipopData);
    series.data.setAll(lollipopData);

    // Add legend (one entry per category)
    let legend = chart.children.push(
      this._baseChartService.createLegend(this.root)
    );

    this._baseChartService.setupLegendAnimation(legend);

    legend.set('maxHeight', 40);

    let markerTemplate = legend.markers.template;
    markerTemplate.set('width', 12);
    markerTemplate.set('height', 12);

    const updateLegend = () => legend.data.setAll(series.dataItems);
    setTimeout(() => updateLegend(), 100);

    // Trigger enhanced chart animation
    setTimeout(() => {
      series.appear(1200, 100);
      chart.appear(1200, 100);
    }, 100);

  }

  private updateChart(data: BarChartData[]) {
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
    }
    this.createChart(data);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
  }
}
